import React, { useRef, useState } from "react";
import {
  Check,
  Columns3,
  FileSpreadsheet,
  LayoutGrid,
  List,
  Rows3,
} from "lucide-react";
import Button from "../ui/Button";
import useClickOutside from "../../hooks/useClickOutside";

/**
 * The strip directly above the table: what's selected, which columns show,
 * how the results are laid out, and the export.
 *
 * Export writes .xlsx, not CSV. The three scopes map to what people actually
 * want: the rows they ticked, the rows the filters left on screen, or
 * everything the search returned.
 */
function ExportMenu({ selectedCount, filteredCount, totalCount, onExport }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false), open);

  const scopes = [
    {
      key: "selected",
      label: `Selected rows (${selectedCount})`,
      disabled: selectedCount === 0,
    },
    {
      key: "filtered",
      label: `Filtered rows (${filteredCount})`,
      disabled: filteredCount === 0,
    },
    {
      key: "all",
      label: `All results (${totalCount})`,
      disabled: totalCount === 0,
    },
  ];

  function choose(key) {
    setOpen(false);
    onExport(key);
  }

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="primary"
        size="sm"
        icon={FileSpreadsheet}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        Export to Excel
      </Button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1.5 w-60 rounded border border-ink-200 bg-white p-1.5 shadow-float"
        >
          <p className="px-2 py-1.5 text-micro font-semibold text-ink-500">
            Which rows go in the file?
          </p>
          {scopes.map((scope) => (
            <button
              key={scope.key}
              type="button"
              role="menuitem"
              disabled={scope.disabled}
              onClick={() => choose(scope.key)}
              className="flex w-full items-center rounded px-2 py-2 text-left text-sm text-ink-800 transition-colors hover:bg-ink-100 disabled:cursor-not-allowed disabled:text-ink-300 disabled:hover:bg-transparent"
            >
              {scope.label}
            </button>
          ))}
          <p className="border-t border-ink-200 px-2 pb-1 pt-2 text-micro leading-snug text-ink-500">
            Downloads an .xlsx workbook with the results and the search that
            produced them.
          </p>
        </div>
      )}
    </div>
  );
}

function ColumnMenu({ columns, visible, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false), open);

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="secondary"
        size="sm"
        icon={Columns3}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        Columns
      </Button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1.5 w-56 rounded border border-ink-200 bg-white p-1.5 shadow-float"
        >
          {columns.map((column) => (
            <button
              key={column.key}
              type="button"
              role="menuitemcheckbox"
              aria-checked={visible[column.key]}
              onClick={() => onToggle(column.key)}
              className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm text-ink-800 transition-colors hover:bg-ink-100"
            >
              {column.label}
              {visible[column.key] && (
                <Check
                  className="h-3.5 w-3.5 text-[#8f5eeb]-600"
                  aria-hidden="true"
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** The three ways to read the same rows. */
export const VIEWS = [
  { key: "list", label: "List", icon: List }, // <-- 1st: List
  { key: "grid", label: "Grid", icon: LayoutGrid }, // <-- 2nd: Grid
  { key: "table", label: "Table", icon: Rows3 }, // <-- 3rd: Table
];

export default function TableToolbar({
  columns,
  visibleColumns,
  onToggleColumn,
  selectedCount,
  filteredCount,
  totalCount,
  onExport,
  onClearSelection,
  view,
  onViewChange,
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-x border-ink-200 bg-paper px-4 py-2.5">
      <div className="flex items-center gap-3">
        {selectedCount > 0 ? (
          <>
            <span className="text-xs font-semibold text-ink-900">
              {selectedCount} selected
            </span>
            <button
              type="button"
              onClick={onClearSelection}
              className="text-xs font-medium text-ink-600 underline underline-offset-2 hover:text-ink-900"
            >
              Clear
            </button>
          </>
        ) : (
          <span className="text-xs text-ink-600">
            {filteredCount === totalCount
              ? `${totalCount} records`
              : `${filteredCount} of ${totalCount} records`}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded border border-ink-200 bg-white p-0.5">
          {VIEWS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => onViewChange(key)}
              aria-pressed={view === key}
              title={`${label} view`}
              className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-semibold transition-colors ${
                view === key
                  ? "bg-ink-900 text-white"
                  : "text-ink-600 hover:bg-ink-100"
              }`}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>

        {view === "table" && (
          <ColumnMenu
            columns={columns}
            visible={visibleColumns}
            onToggle={onToggleColumn}
          />
        )}

        <ExportMenu
          selectedCount={selectedCount}
          filteredCount={filteredCount}
          totalCount={totalCount}
          onExport={onExport}
        />
      </div>
    </div>
  );
}
